// Cancel confirmation modal for Draft SOWs. Mirrors ActivateSowDialog
// shape — no reason field for this dialog. On confirm,
// calls the standard PUT path with `{status: 'cancelled', version}`;
// the backend's state-machine guard accepts the Draft → Cancelled
// transition and updateSowStatus persists the status atomically with a
// version bump.

import { useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import { formatSowCurrency } from "./formatSowCurrency";
import { isConflictError } from "../../../../services/api";
import type { Sow } from "../../../../types";

const ALREADY_TRANSITIONED_COPY =
  "This SOW has already been transitioned. Refresh the page to see the latest status.";

interface CancelSowDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sow: Sow | null;
  gstApplies: boolean | undefined;
  onConfirm: () => Promise<unknown>;
  isSubmitting?: boolean;
}

function fmtDate(v?: string): string {
  if (!v) return "—";
  try {
    return new Date(`${v}T00:00:00`).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return v;
  }
}

export default function CancelSowDialog({
  isOpen,
  onClose,
  sow,
  gstApplies,
  onConfirm,
  isSubmitting,
}: CancelSowDialogProps) {
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      if (isConflictError(err)) {
        setError(ALREADY_TRANSITIONED_COPY);
        return;
      }
      setError(
        err instanceof Error ? err.message : "Failed to cancel SOW.",
      );
    }
  };

  const formatted = sow
    ? formatSowCurrency(sow.originalValue, gstApplies)
    : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={sow ? `Cancel ${sow.number}?` : "Cancel SOW"}
    >
      <div className="space-y-4">
        {sow && (
          <>
            <p className="text-body text-slate-600 dark:text-slate-300">
              Cancelling this Draft SOW marks it as terminated before
              activation. Its value is excluded from contract calculations.
              This action is logged and cannot be reverted.
            </p>
            <dl
              data-testid="cancel-sow-summary"
              className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md bg-slate-50 dark:bg-slate-800/60 p-3"
            >
              <div className="sm:col-span-2">
                <dt className="text-caption text-slate-500 dark:text-slate-400">
                  SOW
                </dt>
                <dd className="text-body text-slate-900 dark:text-slate-100">
                  {sow.name}
                </dd>
              </div>
              <div>
                <dt className="text-caption text-slate-500 dark:text-slate-400">
                  Period
                </dt>
                <dd className="text-body text-slate-900 dark:text-slate-100">
                  {fmtDate(sow.startDate)} – {fmtDate(sow.endDate)}
                </dd>
              </div>
              <div>
                <dt className="text-caption text-slate-500 dark:text-slate-400">
                  Value
                </dt>
                <dd className="text-body text-slate-900 dark:text-slate-100 tabular-nums">
                  {formatted?.value}{" "}
                  <span className="text-caption text-slate-500 dark:text-slate-400">
                    {formatted?.suffix}
                  </span>
                </dd>
              </div>
            </dl>
          </>
        )}
        {error && <Alert variant="error">{error}</Alert>}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Keep as draft
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            loading={isSubmitting}
            disabled={isSubmitting || !sow}
            data-testid="cancel-sow-confirm"
          >
            Cancel SOW
          </Button>
        </div>
      </div>
    </Modal>
  );
}
